/**
 * Centralized Pause/Play Functionality
 * Handles video and scrolling content pause/play controls across multiple sections
 */

document.addEventListener('DOMContentLoaded', function() {

  // ====================================
  // VIDEO PAUSE/PLAY (media-blocks)
  // ====================================
  function initializeVideoControls() {
    const videoSections = document.querySelectorAll('[data-video-controls]');

    videoSections.forEach(function(section) {
      const videoBlocks = section.querySelectorAll('[data-video-block]');

      videoBlocks.forEach(function(block) {
        const playBtn = block.querySelector('[data-video-play]');
        const pauseBtn = block.querySelector('[data-video-pause]');
        const videoElement = block.querySelector('video');

        if (!playBtn || !pauseBtn || !videoElement) {
          return;
        }

        // Update button visibility based on video state
        function updateButtonState() {
          if (videoElement.paused) {
            playBtn.style.display = 'flex';
            pauseBtn.style.display = 'none';
          } else {
            playBtn.style.display = 'none';
            pauseBtn.style.display = 'flex';
          }
        }

        // Set initial state
        updateButtonState();

        // Function to pause the video
        function pauseVideo() {
          videoElement.pause();
          updateButtonState();
        }

        // Function to play the video
        function playVideo() {
          videoElement.play().then(function() {
            updateButtonState();
          }).catch(function(error) {
            updateButtonState();
          });
        }

        // Pause button click handler
        pauseBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          pauseVideo();
        });

        // Play button click handler
        playBtn.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          playVideo();
        });

        // Keyboard accessibility
        playBtn.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            playVideo();
          }
        });

        pauseBtn.addEventListener('keydown', function(e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            pauseVideo();
          }
        });

        // Listen to video events to sync button state
        videoElement.addEventListener('play', updateButtonState);
        videoElement.addEventListener('pause', updateButtonState);
        videoElement.addEventListener('ended', updateButtonState);
      });
    });
  }

  // ====================================
  // MARQUEE/SCROLLING CONTENT PAUSE/PLAY
  // ====================================
  function initializeScrollingControls() {
    const scrollingSections = document.querySelectorAll('[data-scrolling-controls]');

    scrollingSections.forEach(function(section) {
      const sectionId = section.getAttribute('data-section-id');
      const sectionElement = document.getElementById(sectionId);
      const playBtn = section.querySelector('[data-scrolling-play]');
      const pauseBtn = section.querySelector('[data-scrolling-pause]');
      const marqueeElement = section.querySelector('marquee-text');

      if (!sectionElement || !playBtn || !pauseBtn || !marqueeElement) {
        return;
      }

      let isPaused = false;
      let currentAnimation = null;

      // Function to get the animation from the marquee element
      function getMarqueeAnimation() {
        if (currentAnimation) return currentAnimation;

        setTimeout(() => {
          if (marqueeElement.shadowRoot) {
            const scroller = marqueeElement.shadowRoot.querySelector('[part="scroller"]');
            if (scroller) {
              const animations = scroller.getAnimations();
              if (animations.length > 0) {
                currentAnimation = animations[0];
              }
            }
          }
        }, 100);

        return currentAnimation;
      }

      // Function to pause the marquee
      function pauseMarquee() {
        sectionElement.classList.add('scrolling-banner-paused');

        const animation = getMarqueeAnimation();
        if (animation) {
          animation.pause();
        } else if (marqueeElement.hasAttribute('pause-on-hover')) {
          marqueeElement.dispatchEvent(new PointerEvent('pointerenter', {
            bubbles: true,
            cancelable: true
          }));
        } else {
          marqueeElement.style.animationPlayState = 'paused';
        }

        isPaused = true;
      }

      // Function to resume the marquee
      function resumeMarquee() {
        sectionElement.classList.remove('scrolling-banner-paused');

        const animation = getMarqueeAnimation();
        if (animation) {
          animation.play();
        } else if (marqueeElement.hasAttribute('pause-on-hover')) {
          marqueeElement.dispatchEvent(new PointerEvent('pointerleave', {
            bubbles: true,
            cancelable: true
          }));
        } else {
          marqueeElement.style.animationPlayState = 'running';
        }

        isPaused = false;
      }

      // Pause functionality
      pauseBtn.addEventListener('click', function() {
        pauseMarquee();
      });

      // Play functionality
      playBtn.addEventListener('click', function() {
        resumeMarquee();
      });

      // Initialize animation detection
      setTimeout(() => {
        getMarqueeAnimation();
      }, 500);

      // Keyboard accessibility
      playBtn.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          playBtn.click();
        }
      });

      pauseBtn.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          pauseBtn.click();
        }
      });
    });
  }

  // ====================================
  // TEXT-WITH-MEDIA VIDEO CONTROLS
  // ====================================
  function initializeTextMediaControls() {
    const textMediaSections = document.querySelectorAll('[data-text-media-controls]');

    textMediaSections.forEach(function(section) {
      const primaryVideoContainer = section.querySelector('[data-primary-video]');
      const secondaryVideoContainer = section.querySelector('[data-secondary-video]');

      // Handle primary video
      if (primaryVideoContainer) {
        const videoElement = primaryVideoContainer.querySelector('video');
        const playBtn = section.querySelector('[data-primary-play]');
        const pauseBtn = section.querySelector('[data-primary-pause]');

        if (videoElement && playBtn && pauseBtn) {
          setupVideoControl(videoElement, playBtn, pauseBtn);
        }
      }

      // Handle secondary video
      if (secondaryVideoContainer) {
        const videoElement = secondaryVideoContainer.querySelector('video');
        const playBtn = section.querySelector('[data-secondary-play]');
        const pauseBtn = section.querySelector('[data-secondary-pause]');

        if (videoElement && playBtn && pauseBtn) {
          setupVideoControl(videoElement, playBtn, pauseBtn);
        }
      }
    });
  }

  // Reusable video control setup
  function setupVideoControl(videoElement, playBtn, pauseBtn) {
    function updateButtonState() {
      if (videoElement.paused) {
        playBtn.style.display = 'flex';
        pauseBtn.style.display = 'none';
      } else {
        playBtn.style.display = 'none';
        pauseBtn.style.display = 'flex';
      }
    }

    updateButtonState();

    function pauseVideo() {
      videoElement.pause();
      updateButtonState();
    }

    function playVideo() {
      videoElement.play().then(updateButtonState).catch(updateButtonState);
    }

    pauseBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      pauseVideo();
    });

    playBtn.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation();
      playVideo();
    });

    // Keyboard accessibility
    playBtn.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        playVideo();
      }
    });

    pauseBtn.addEventListener('keydown', function(e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        pauseVideo();
      }
    });

    // Listen to video events
    videoElement.addEventListener('play', updateButtonState);
    videoElement.addEventListener('pause', updateButtonState);
    videoElement.addEventListener('ended', updateButtonState);
  }

  // Initialize all controls
  initializeVideoControls();
  initializeScrollingControls();
  initializeTextMediaControls();
});
